import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, FolderSearchIcon, GlobeIcon, MessagesSquareIcon, PencilRulerIcon, UserMinusIcon, UsersIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { pageSamples as samples } from './samples';

// ANGIELSKI GOTOWY™ copy (Polish). data/wf.ts and data/content.ts are kept untouched and are not imported.
// ANGIELSKI GOTOWY™ product mockup (WebP, transparent), hosted in Impultienda Recursos.
export const PRODUCT_IMAGE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/488e14ea-b7e9-483a-92ef-f01d9b9d9e6b/1792379126895_angielski-gotowy-mockup-pudelko-6-tomow-340d5cc9.webp';
export const heroGuarantees = ['Natychmiastowy dostęp do pobrania', 'Jednorazowa płatność', 'Bez subskrypcji', '7 dni gwarancji satysfakcji'];
export const stats = [{
  value: '6',
  label: 'Cyfrowych tomów do angielskiego'
}, {
  value: '12',
  label: 'Lekcji zaplanowanych minuta po minucie'
}, {
  value: '8',
  label: 'Klas – od 1 do 8'
}, {
  value: '5',
  label: 'Bonusów w zestawie'
}, {
  value: '100',
  label: 'Gotowych komentarzy do oceniania'
}];
export const differenceStats = [{
  value: 'Klasy 1–8',
  label: 'Jeden system dla całej szkoły podstawowej'
}, {
  value: '6 cyfrowych tomów',
  label: 'Od planowania i speakingu po ocenianie'
}, {
  value: 'PDF-y do wydruku',
  label: 'Do korzystania na ekranie lub w wersji papierowej'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Niedziele znikają na planowaniu',
  text: 'Godziny przed komputerem, żeby zaplanować lekcje angielskiego na kolejny tydzień – a i tak nie wszystko jest gotowe.'
}, {
  title: 'Godziny szukania kart pracy i ćwiczeń',
  text: 'Materiały z różnych stron internetowych są za łatwe, za trudne albo trzeba je przerabiać, zanim nadadzą się na lekcję.'
}, {
  title: 'Różne poziomy językowe w jednej klasie',
  text: 'Jedni uczniowie się nudzą, inni nie nadążają – trudno przygotować zadania, które sprawdzą się u wszystkich.'
}, {
  title: 'Cisza, gdy trzeba mówić po angielsku',
  text: 'Część klasy unika mówienia, a Ty szukasz sposobu, żeby każdy uczeń odważył się odezwać.'
}, {
  title: 'Gramatyka, która nie zostaje w głowie',
  text: 'Kolejne ćwiczenia z podręcznika nie wystarczają, a na wymyślanie nowych brakuje czasu.'
}, {
  title: 'Ocenianie i komentarze zajmują czas',
  text: 'Testy, rubryki i opisowe komentarze dla każdego ucznia to kolejna praca po lekcjach.'
}];
export const afterItems = ['Uporządkowany system do angielskiego dla całej szkoły podstawowej', 'Plan roczny i 12 lekcji zaplanowanych minuta po minucie', 'Karty rozmów i scenki, które ośmielają do mówienia', 'Czytanie i pisanie krok po kroku', 'Gramatyka i słownictwo bez nudy', 'Kultura, piosenki, święta i projekty ze świata anglojęzycznego', 'Zadania w trzech poziomach: Wsparcie, Podstawa, Wyzwanie', 'Testy A/B, rubryki i 100 gotowych komentarzy', 'Instrukcje po polsku, materiały dla ucznia po angielsku – gotowe do wydruku'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Niedziele spędzasz na planowaniu',
  text: 'Czy poświęcasz część weekendu na przygotowanie lekcji angielskiego na kolejny tydzień?'
}, {
  icon: FolderSearchIcon,
  title: 'Szukasz materiałów na dziesięciu stronach',
  text: 'Czy ciągle szukasz kart pracy, gier i ćwiczeń dopasowanych do wieku i poziomu uczniów?'
}, {
  icon: UsersIcon,
  title: 'W klasie są bardzo różne poziomy',
  text: 'Czy trudno Ci prowadzić jedną lekcję, gdy jedni mówią swobodnie, a inni dopiero poznają podstawy?'
}, {
  icon: UserMinusIcon,
  title: 'Uczniowie boją się mówić',
  text: 'Czy w klasie zapada cisza, gdy prosisz uczniów o rozmowę po angielsku?'
}, {
  icon: BookOpenTextIcon,
  title: 'Gramatyka nudzi, a podręcznik nie wystarcza',
  text: 'Czy potrzebujesz świeżych pomysłów, żeby gramatyka i słówka zostały w głowie na dłużej?'
}, {
  icon: PencilRulerIcon,
  title: 'Ocenianie i zastępstwa to dodatkowa praca',
  text: 'Czy chcesz mieć pod ręką testy, komentarze i gotowe lekcje na zastępstwo?'
}];

// Spine colours mirror the box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Tom 01',
  level: 'Planowanie',
  title: 'Plan roczny + 12 lekcji minuta po minucie',
  tagline: 'Wiesz dokładnie, co robić – przez cały rok szkolny.',
  intro: 'Pierwszy tom zawiera plan roczny dla klas 1–8 oraz dwanaście gotowych lekcji angielskiego zaplanowanych minuta po minucie.',
  items: ['Plan roczny dla klas 1–3, 4–6 i 7–8', '12 gotowych lekcji angielskiego', 'Przebieg lekcji minuta po minucie', 'Instrukcje dla nauczyciela po polsku', 'Materiały dla ucznia po angielsku', 'Klucze odpowiedzi'],
  outro: 'Otwierasz, wybierasz lekcję i wchodzisz do klasy z jasnym planem.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Tom 02',
  level: 'Speaking',
  title: 'Speaking – karty rozmów i scenki',
  tagline: 'Uczniowie w końcu zaczynają mówić po angielsku.',
  intro: 'Drugi tom to karty rozmów i scenki, które łatwo wpleść w każdą lekcję – i które zachęcają do mówienia całą klasę.',
  items: ['Karty rozmów do wydruku', 'Scenki do odegrania w parach i grupach', 'Zadania w trzech poziomach', 'Dla klas 1–3, 4–6 i 7–8', 'Instrukcje po polsku', 'Materiały dla ucznia po angielsku'],
  outro: 'Gotowe pomysły, które ośmielają do mówienia także cichszych uczniów.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Tom 03',
  level: 'Czytanie i pisanie',
  title: 'Czytanie i pisanie krok po kroku',
  tagline: 'Rozwijasz czytanie i pisanie – krok po kroku.',
  intro: 'Trzeci tom prowadzi uczniów krok po kroku: od zrozumienia tekstu po napisanie własnej wypowiedzi po angielsku.',
  items: ['Czytanie ze zrozumieniem', 'Pisanie krok po kroku', 'Zadania w trzech poziomach', 'Klucze odpowiedzi', 'Instrukcje po polsku', 'Karty do wydruku'],
  outro: 'Uporządkowany materiał, dzięki któremu każda lekcja czytania i pisania ma sens i cel.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Tom 04',
  level: 'Gramatyka',
  title: 'Gramatyka i słownictwo bez nudy',
  tagline: 'Gramatyka i słówka, które zostają w głowie.',
  intro: 'Czwarty tom zawiera ćwiczenia z gramatyki i słownictwa z zadaniami w trzech poziomach: Wsparcie, Podstawa i Wyzwanie.',
  items: ['Ćwiczenia z gramatyki', 'Ćwiczenia ze słownictwa', 'Trzy poziomy trudności', 'Klucze odpowiedzi', 'Karty pracy do wydruku', 'Dla klas 1–8'],
  outro: 'Praktyczne wsparcie w pracy z klasą o różnym poziomie językowym.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Tom 05',
  level: 'Kultura',
  title: 'Świat anglojęzyczny – kultura, piosenki, święta, projekty',
  tagline: 'Angielski to także ciekawość świata.',
  intro: 'Piąty tom przybliża uczniom świat anglojęzyczny: kulturę, piosenki, święta i projekty do realizacji w klasie.',
  items: ['Kultura krajów anglojęzycznych', 'Piosenki', 'Święta', 'Projekty dla uczniów', 'Instrukcje po polsku', 'Materiały dla ucznia po angielsku'],
  outro: 'Treści, które ożywiają lekcje i pokazują uczniom, po co uczyć się angielskiego.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Tom 06',
  level: 'Ocenianie',
  title: 'Ocenianie – testy A/B, rubryki i 100 gotowych komentarzy',
  tagline: 'Oceniasz szybciej, jaśniej i sprawiedliwiej.',
  intro: 'Szósty tom zawiera testy w dwóch wersjach (A i B), rubryki oceniania i 100 gotowych komentarzy.',
  items: ['Testy w wersji A i B', 'Rubryki oceniania', '100 gotowych komentarzy', 'Klucze odpowiedzi', 'Materiały do wydruku'],
  outro: 'Praktyczne wsparcie w ocenianiu i informacji zwrotnej dla uczniów.',
  note: 'Testy zostały opracowane niezależnie i nie są oficjalnymi narzędziami Ministerstwa Edukacji Narodowej.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 cyfrowych tomów dla klas 1–8',
  text: 'System sześć w jednym do angielskiego, który pomaga planować i prowadzić lekcje przez cały rok szkolny.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Gotowe lekcje',
  text: 'Nie zaczynasz od pustej kartki. Dwanaście lekcji zaplanowanych minuta po minucie pozwala szybko ruszyć z pracą.'
}, {
  icon: MessagesSquareIcon,
  title: 'Speaking, który działa',
  text: 'Karty rozmów i scenki, które zachęcają całą klasę do mówienia po angielsku.'
}, {
  icon: PencilRulerIcon,
  title: 'Gramatyka i słownictwo bez nudy',
  text: 'Zadania w trzech poziomach – Wsparcie, Podstawa, Wyzwanie – z kluczami odpowiedzi.'
}, {
  icon: GlobeIcon,
  title: 'Świat anglojęzyczny',
  text: 'Kultura, piosenki, święta i projekty, które ożywiają lekcje angielskiego.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Wsparcie w ocenianiu',
  text: 'Testy A/B, rubryki oceniania i 100 gotowych komentarzy.'
}, {
  icon: CalendarCheckIcon,
  title: 'Plan roczny',
  text: 'Wsparcie w zorganizowaniu lekcji angielskiego na cały rok szkolny – dla klas 1–3, 4–6 i 7–8.'
}];

// Real sample pages live in data/samples.ts (empty for now – the section hides).
export const pageSamples = samples;
export const pageTypes = ['Gotowe lekcje', 'Karty rozmów i scenki', 'Ćwiczenia i karty pracy', 'Materiały do oceniania'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Angielski w 5 minut',
  text: '60 gotowych warm-upów i fillerów, które poprowadzisz od razu na początku lub na końcu lekcji – bez przygotowania.',
  items: ['60 warm-upów i fillerów', 'Dla klas 1–3, 4–6 i 7–8', 'Instrukcje po polsku, zwroty po angielsku', 'Do użycia od razu'],
  benefit: 'Sprawny początek – i dobry koniec – każdej lekcji.'
}, {
  title: 'Zastępstwa z angielskiego bez stresu',
  text: '15 gotowych lekcji, które przeprowadzi każdy nauczyciel zastępujący – także taki, który nie uczy angielskiego.',
  items: ['Po 5 lekcji dla klas 1–3, 4–6 i 7–8', 'Plan minuta po minucie', 'Karty pracy i klucze odpowiedzi', 'Do wydruku'],
  benefit: 'Spokój, gdy nie możesz być na lekcji osobiście.'
}, {
  title: 'Bingo i gry językowe',
  text: '8 gotowych gier bingo i 12 gier klasowych, które utrwalają słownictwo i zwroty po angielsku.',
  items: ['8 plansz bingo do wydruku', '12 gier klasowych', 'Karty obrazkowo-wyrazowe', 'Wersje łatwiejsze i trudniejsze'],
  benefit: 'Uczniowie mówią po angielsku – i dobrze się przy tym bawią.'
}, {
  title: 'Komunikacja z rodzicami',
  text: '25 gotowych wiadomości do rodziców – na e-mail, Librusa, Vulcana lub SMS – na codzienne i trudne sytuacje.',
  items: ['25 gotowych wiadomości', 'Szablony do skopiowania', 'Porady, jak wspierać angielski w domu'],
  benefit: 'Mniej czasu na pisanie do rodziców.'
}, {
  title: 'Plakaty i zwroty do klasy',
  text: '30 kolorowych plakatów do powieszenia w klasie: słownictwo, gramatyka i najważniejsze zwroty klasowe.',
  items: ['30 plakatów do wydruku', 'Format A4 lub A3'],
  benefit: 'Klasa, w której angielski widać na co dzień.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Otwórz ANGIELSKI GOTOWY™',
  text: 'Pobierz pakiet i otwórz potrzebny tom.'
}, {
  title: 'Wybierz lekcję i materiały',
  text: 'Wybierz lekcję, kartę rozmów, ćwiczenia lub test dopasowany do Twojej klasy.'
}, {
  title: 'Wydrukuj',
  text: 'Wydrukuj to, czego potrzebujesz, albo korzystaj z materiałów na ekranie.'
}, {
  title: 'Prowadź lekcję',
  text: 'Wejdź do klasy z jasnym planem.'
}];
export const audienceItems = ['Uczysz angielskiego w klasach 1–3, 4–6 lub 7–8', 'Chcesz mieć uporządkowany angielski na cały rok szkolny', 'Nie chcesz co tydzień planować lekcji od zera', 'Chcesz, żeby także cichsi uczniowie zaczęli mówić', 'Szukasz zadań na różne poziomy w jednej klasie', 'Chcesz mieć pod ręką testy, rubryki i komentarze', 'Chcesz zaoszczędzić czas na planowaniu', 'Chcesz wchodzić na każdą lekcję z gotowym planem'];
export const timeValueTasks = ['Szukanie kart pracy i ćwiczeń na różnych stronach', 'Planowanie lekcji', 'Wymyślanie zadań do mówienia', 'Przygotowywanie lekcji na zastępstwa', 'Testy, sprawdzanie i komentarze do ocen'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 cyfrowych tomów',
  detail: 'Planowanie, speaking, czytanie i pisanie, gramatyka i słownictwo, świat anglojęzyczny i ocenianie'
}, {
  label: '5 bonusów bez dodatkowych kosztów',
  detail: 'Warm-upy, zastępstwa, bingo i gry, komunikacja z rodzicami i plakaty'
}, {
  label: 'Dla klas 1–8',
  detail: 'Gotowe lekcje, karty rozmów, ćwiczenia i materiały do oceniania'
}];
export const offerIncludes = ['Natychmiastowy dostęp do pobrania', 'Pliki PDF do zapisania', 'Materiały do wydruku', 'Nie wysyłamy produktu fizycznego'];
export const PRICE = '99 zł';
export const OLD_PRICE = '199 zł';

// Only add authentic reviews of ANGIELSKI GOTOWY™. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [{
  quote: 'Najbardziej doceniam to, że nie muszę wymyślać całej lekcji od początku. Gotowy scenariusz i karty pracy dają mi bazę, którą mogę dopasować do swojej klasy.',
  name: 'Monika Dąbrowska',
  location: 'Warszawa'
}, {
  quote: 'Karty rozmów i scenki to moja ulubiona część pakietu. Uczniowie mają konkretną sytuację i temat do rozmowy, a ja nie muszę za każdym razem przygotowywać nowych dialogów.',
  name: 'Agnieszka Lewandowska',
  location: 'Kraków'
}, {
  quote: 'W jednej klasie mam uczniów na bardzo różnych poziomach. Podział na Wsparcie, Podstawę i Wyzwanie pomaga mi dobrać zadania tak, żeby każdy miał nad czym pracować.',
  name: 'Tomasz Kamiński',
  location: 'Wrocław'
}, {
  quote: 'Bardzo przydają mi się krótkie rozgrzewki językowe i bingo. Dobrze mieć pod ręką pomysł na początek zajęć albo kilka minut, które zostają przed dzwonkiem.',
  name: 'Joanna Szymańska',
  location: 'Poznań'
}, {
  quote: 'Sięgam przede wszystkim po materiały do oceniania. Testy A/B, kryteria i przykładowe komentarze pomagają mi uporządkować pracę. Komentarze dopasowuję do konkretnego ucznia, ale nie zaczynam już od zera.',
  name: 'Aleksandra Woźniak',
  location: 'Gdańsk'
}];
export const faqs = [{
  q: 'Jak otrzymam materiały?',
  a: 'ANGIELSKI GOTOWY™ jest produktem cyfrowym. Po zakupie otrzymujesz dostęp do plików PDF, które możesz pobrać i zapisać. Możesz korzystać z nich na komputerze, tablecie lub telefonie i drukować to, czego potrzebujesz.'
}, {
  q: 'Czy ANGIELSKI GOTOWY™ jest przygotowany z myślą o polskiej szkole?',
  a: 'Tak. Materiały zostały przygotowane dla nauczycieli angielskiego w polskich szkołach podstawowych, w klasach 1–8. To niezależny materiał dydaktyczny, a nie oficjalny produkt Ministerstwa Edukacji Narodowej (MEN).'
}, {
  q: 'W jakim języku są materiały?',
  a: 'Instrukcje dla nauczyciela są po polsku, a materiały dla uczniów – po angielsku. Do zadań dołączone są klucze odpowiedzi.'
}, {
  q: 'Czy pakiet sprawdzi się w klasach 1–8?',
  a: 'Tak. Materiały są podzielone na klasy 1–3, 4–6 i 7–8 – wybierasz te, które pasują do Twojej klasy i zagadnienia, które realizujesz.'
}, {
  q: 'Czy naprawdę mogę przygotować lekcję w 5 minut?',
  a: 'ANGIELSKI GOTOWY™ został stworzony po to, by przyspieszyć przygotowanie lekcji. Możesz otworzyć tom, wybrać gotową lekcję lub aktywność i zapoznać się z nią w około pięć minut. Czas może się różnić w zależności od treści lekcji, klasy i potrzebnych dostosowań.'
}, {
  q: 'Czy materiałów wystarczy na cały rok szkolny?',
  a: 'ANGIELSKI GOTOWY™ zawiera plan roczny i materiały, z których możesz korzystać przez cały rok szkolny. To Ty wybierasz, łączysz i dostosowujesz je do swojego planu i potrzeb uczniów.'
}, {
  q: 'Czy są gotowe ćwiczenia do mówienia?',
  a: 'Tak. Pakiet zawiera karty rozmów i scenki, a także ćwiczenia z czytania i pisania, gramatyki i słownictwa oraz materiały o świecie anglojęzycznym – wraz z kartami do wydruku.'
}, {
  q: 'Czy pakiet pomaga w ocenianiu?',
  a: 'Tak. Pakiet zawiera testy w wersjach A i B, rubryki oceniania, klucze odpowiedzi i 100 gotowych komentarzy.'
}, {
  q: 'Czy pakiet przyda się przed egzaminem ósmoklasisty?',
  a: 'W materiałach dla klas 7–8 znajdziesz m.in. ćwiczenia w stylu egzaminacyjnym, które możesz wpleść w lekcje. To niezależne materiały – nie są oficjalnymi arkuszami egzaminacyjnymi ani materiałami MEN.'
}, {
  q: 'Czy mogę korzystać z materiałów w klasie o różnym poziomie językowym?',
  a: 'Tak. Zadania są przygotowane w trzech poziomach – Wsparcie, Podstawa i Wyzwanie – a Ty wybierasz i dostosowujesz je do możliwości i potrzeb uczniów.'
}, {
  q: 'Czy muszę wszystko drukować?',
  a: 'Nie. To Ty decydujesz, które strony wydrukować, a z części materiałów możesz korzystać na ekranie.'
}, {
  q: 'Czy pięć bonusów jest w cenie?',
  a: `Tak. Wszystkich pięć bonusów jest w pakiecie bez dodatkowych kosztów – całość za ${PRICE}.`
}, {
  q: 'Czy to subskrypcja?',
  a: 'Nie. Płacisz jednorazowo i otrzymujesz dostęp do materiałów cyfrowych. Bez żadnych opłat cyklicznych.'
}, {
  q: 'Czy mogę zapisać pliki?',
  a: 'Tak. Możesz pobrać i zapisać pliki PDF, aby korzystać z nich dalej zgodnie z warunkami korzystania z produktu.'
}, {
  q: 'Czy mogę korzystać z materiałów na telefonie lub tablecie?',
  a: 'Tak. Pliki PDF otworzysz na komputerze, tablecie i telefonie. Aby sprawdzić układ stron przed wydrukiem, polecamy komputer lub tablet.'
}, {
  q: 'Czy jest gwarancja?',
  a: 'Tak. ANGIELSKI GOTOWY™ objęty jest 7-dniową gwarancją satysfakcji. Jeśli materiały nie spełnią Twoich oczekiwań, możesz skontaktować się z nami i poprosić o zwrot pieniędzy w okresie gwarancji, zgodnie z jej warunkami.'
}];
export const footerLinks = ['Regulamin', 'Polityka prywatności', 'Polityka zwrotów', 'Kontakt'];
